import { HTMLAttributes, useCallback } from 'react';
import { Row, Col, Tag, Popover } from 'antd';
import { useTranslate } from '@tolgee/react';
import CancelApprovalBtns from 'views/Refund/components/CancelApprovalBtns';
import { useTradeCancelInfo } from './hooks/useTradeCancelInfo';
import { useTradeListContext } from './TradeContext';

export interface TradeCancelInfoProps extends HTMLAttributes<HTMLElement> {
  trade: Conversation.Trade.Vo;
  conversation: Conversation.Conversation;
}

function TradeCancelInfo(props: TradeCancelInfoProps) {
  const { trade, conversation, ...otherProps } = props;
  const { t } = useTranslate();
  const { refreshTrade } = useTradeListContext();
  const { storeId, tunnelAccountId } = conversation;

  const { data } = useTradeCancelInfo({ trade, storeId });

  const { internalBuyerId, externalTradeId } = trade;
  const {
    shouldRender,
    isRefundStatus,
    cancelType,
    cancelReasonLabel,
    formattedModifiedTime,
    showCompletedTag,
    operationUserName,
    formattedOperationTime,
    showApprovalBtns,
    approvalBtnParams,
  } = data;

  // 刷新订单回调
  const handleRefreshTrade = useCallback(() => {
    refreshTrade(
      {
        page_no: 1,
        page_size: 1,
        internal_buyer_id: internalBuyerId,
        internal_store_id: storeId,
        tunnelAccountId: tunnelAccountId,
        external_trade_ids: externalTradeId,
      },
      'SHOPEE',
    );
  }, [refreshTrade, internalBuyerId, storeId, tunnelAccountId, externalTradeId]);

  if (!shouldRender) {
    return null;
  }

  return (
    <div {...otherProps}>
      {/* 标题行：取消信息标题 + 取消类型标签 */}
      <div className="flex justify-between items-center text-xs text-[#515359] mb-1">
        <span>
          {isRefundStatus
            ? t('scene.chatWorkspace.orderTab.returnRefund.label.reasonTitle')
            : t('scene.chatWorkspace.orderTab.returnRefund.title.cancelInfo')}
        </span>
        {cancelType && (
          <span className="text-[#f2a100]">
            {t('scene.chatWorkspace.orderTab.returnRefund.label.cancelRole', {
              role: t(`cancelType.${cancelType}`),
            })}
          </span>
        )}
      </div>

      {/* 取消原因详情卡片 */}
      {(cancelReasonLabel || formattedModifiedTime) && (
        <section className="block ml-1 py-2 px-4 text bg-[#f7f9fa] rounded leading-[18px]">
          {cancelReasonLabel && <div className="text-[#515359]">{cancelReasonLabel}</div>}
          {formattedModifiedTime && (
            <div className="text-[#84868C] mt-1">{formattedModifiedTime}</div>
          )}
        </section>
      )}

      {/* 操作按钮区域 */}
      <div className="flex items-center justify-end mt-2 gap-2">
        {/* 已完成状态标签 */}
        {showCompletedTag && (
          <Popover
            content={
              <Row justify="space-between" gutter={50}>
                <Col>{operationUserName}</Col>
                {formattedOperationTime && <Col>{formattedOperationTime}</Col>}
              </Row>
            }
          >
            <Tag color="success">{t('orderStatus.COMPLETE')}</Tag>
          </Popover>
        )}

        {/* 取消审批按钮 */}
        {showApprovalBtns && (
          <CancelApprovalBtns
            internalStoreId={approvalBtnParams.internalStoreId}
            externalStoreId={approvalBtnParams.externalStoreId}
            orderId={approvalBtnParams.orderId}
            platform={approvalBtnParams.platform}
            region={approvalBtnParams.region}
            operate={approvalBtnParams.operate}
            onUpdate={handleRefreshTrade}
            buttonProps={{ type: 'primary', size: 'small', className: '!ml-2' }}
          />
        )}
      </div>
    </div>
  );
}

export default TradeCancelInfo;
